import React, { memo, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import FixedHeaderTable from 'src/design-system/components/molecules/FixedHeaderTable/FixedHeaderTable';
import { calculatePivotColumnWidth, GetChartConfigData } from './utils';

const useStyles = createUseStyles({
  pivotTableWrapper: {
    '& .ant-table-thead > tr > th': {
      backgroundColor: 'var(--column-header-bg) !important',
      color: 'var(--column-header-font) !important',
      borderColor: 'var(--column-header-border) !important',
    },
    '& .ant-table-thead > tr': {
      backgroundColor: 'var(--column-header-bg) !important',
    },
    '& .ant-table-tbody > tr > td': {
      backgroundColor: 'var(--row-bg) !important',
      color: 'var(--row-font-color) !important',
      borderColor: 'var(--cell-border) !important',
    },
    '& .ant-table-tbody > tr:nth-child(even) > td': {
      backgroundColor: 'var(--alternate-row-color) !important',
    },
  },
});

const PivotTableViz = (props) => {
  const { t } = useTranslation();
  const {
    chartData,
    formData,
    chartConfig,
    containerWidth,
    columnWidth,
    chartsFromDashboard,
  } = props;
  
  const configData = GetChartConfigData({
    chartConfig,
    configType: 'pivotTable',
    formData,
    chartsFromDashboard,
  });

  const palette = {
    brandColor: '#ffffff',
    semanticColors: {},
    others: {},
    basicColors: configData?.graphic?.color?.data,
  };

  const paletteColors = palette.basicColors;
  const classes = useStyles();

  const dataSource = useMemo(() => {
    if (!chartData) {
      return [];
    }
    if (Array.isArray(chartData)) {
      return chartData;
    }
    if (typeof chartData === 'object') {
      return [chartData];
    }
    return [];
  }, [chartData]);

  const columns = useMemo(() => {
    if (!formData?.meta || !Array.isArray(formData.meta)) {
      return [];
    }

    const distinctColumns = Array.from(
      new Set(
        dataSource
          .flatMap((row) => Object.keys(row))
          .filter((key) => {
            const metaItem = formData.meta.find((m) => m.name === key || m.metric === key);
            return metaItem;
          })
      )
    );

    return distinctColumns.map((colKey) => {
      const metaItem = formData.meta.find(
        (m) => m.name === colKey || m.metric === colKey
      );
      
      const isNumberColumn = metaItem?.type === 'number' || 
        (metaItem?.operation?.type === 'number') ||
        (dataSource.length > 0 && typeof dataSource[0]?.[colKey] === 'number');
      
      const align = isNumberColumn 
        ? configData?.graphic?.numberAlign?.data
        : configData?.graphic?.textAlign?.data;
      
      return {
        title: metaItem?.name || colKey,
        dataIndex: colKey,
        key: colKey,
        width: columnWidth?.[colKey] || 150,
        align: align,
      };
    });
  }, [formData, dataSource, columnWidth, configData]);

  const columnWidths = useMemo(() => {
    if (!containerWidth || !formData?.meta) {
      return {};
    }

    const distinctColCount = columns.length;
    return calculatePivotColumnWidth({
      metrics: formData.meta.reduce((acc, meta) => {
        acc[meta.metric] = meta;
        return acc;
      }, {}),
      dashboardColumnWidth: columnWidth,
      columns: columns.map((c) => c.dataIndex),
      containerWidth,
      distinctColCount,
    });
  }, [containerWidth, formData, columnWidth, columns]);

  const finalColumns = useMemo(() => {
    return columns.map((col) => ({
      ...col,
      width: columnWidths[col.dataIndex] || col.width,
    }));
  }, [columns, columnWidths]);

  if (!dataSource || dataSource.length === 0) {
    return <Empty description={t('No data available')} />;
  }

  if (!finalColumns || finalColumns.length === 0) {
    return <Empty description={t('No columns available')} />;
  }

  const themeCfg = { palette };

  if (!paletteColors || !Array.isArray(paletteColors)) {
    return (
      <FixedHeaderTable
        {...({
          dataSource,
          columns: finalColumns,
          pagination: false,
          rowKey: (record: any, index: number) => `pivot-table-row-${index}`,
          scroll: { x: 'max-content', y: '100%' },
          themeCfg,
        } as any)}
      />
    );
  }

  return (
    <div 
      className={classes.pivotTableWrapper}
      style={paletteColors && Array.isArray(paletteColors) ? {
        '--column-header-bg': paletteColors[0],
        '--column-header-font': paletteColors[1],
        '--row-bg': paletteColors[2],
        '--alternate-row-color': paletteColors[3],
        '--row-font-color': paletteColors[4],
        '--column-header-border': paletteColors[5],
        '--cell-border': paletteColors[6],
      } as React.CSSProperties : undefined}
    >
      <FixedHeaderTable
        {...({
          dataSource,
          columns: finalColumns,
          pagination: false,
          rowKey: (record: any, index: number) => `pivot-table-row-${index}`,
          scroll: { x: 'max-content', y: '100%' },
          themeCfg,
        } as any)}
      />
    </div>
  );
};

const areEqual = (prevProps, nextProps) => {
  if (
    prevProps.formData === nextProps.formData &&
    prevProps.chartData === nextProps.chartData &&
    prevProps.containerWidth === nextProps.containerWidth &&
    prevProps.columnWidth === nextProps.columnWidth &&
    prevProps.chartConfig === nextProps.chartConfig
  ) {
    return true;
  }
  return false;
};

export default memo(PivotTableViz, areEqual);

